import { nanoid } from 'nanoid';
import { ImageUtil } from '../util.image';

/**
 * @COMMENT - 하드코딩 된 상수들을 분리하여 코드 유지보수성 향상
 * SVG 합성 관련 상수들
 */
export const SVG_COMPOSITE_CONSTANTS = {
  NAMESPACES: {
    SVG: 'http://www.w3.org/2000/svg',
    XLINK: 'http://www.w3.org/1999/xlink',
  },
  MATERIAL_TYPES: {
    OVERLAY: 'overlay' as const,
    MULTIPLY: 'multiply' as const,
  },
  SELECTORS: {
    CUT_PATH: 'path[stroke-dasharray], path',
    DESIGN_USE: 'use[href="#design-image-svg"], use[xlink\\:href="#design-image-svg"]',
  },
  ATTRIBUTES: {
    STROKE_PROPS: [
      'stroke',
      'stroke-width',
      'stroke-linecap',
      'stroke-linejoin',
      'stroke-miterlimit',
      'stroke-dasharray',
      'stroke-dashoffset',
    ] as const,
  },
} as const;

/**
 * 재질 타입 정의
 */
export type MaterialType =
  (typeof SVG_COMPOSITE_CONSTANTS.MATERIAL_TYPES)[keyof typeof SVG_COMPOSITE_CONSTANTS.MATERIAL_TYPES];

/**
 * 재질 정보 인터페이스
 */
export interface MaterialInfo {
  source: string;
  type: MaterialType;
}

/**
 * @COMMENT - 정의되지 않은 타입 핸들링을 위한 유틸리티 함수들
 * 안전한 재질 타입 검증 및 기본값 제공
 */
export const validateMaterialType = (type: string): MaterialType => {
  if (type === SVG_COMPOSITE_CONSTANTS.MATERIAL_TYPES.OVERLAY) {
    return SVG_COMPOSITE_CONSTANTS.MATERIAL_TYPES.OVERLAY;
  }
  if (type === SVG_COMPOSITE_CONSTANTS.MATERIAL_TYPES.MULTIPLY) {
    return SVG_COMPOSITE_CONSTANTS.MATERIAL_TYPES.MULTIPLY;
  }
  // 정의되지 않은 타입은 기본값으로 처리
  console.warn(`알 수 없는 재질 타입 '${type}', 기본값 'overlay'로 처리합니다`);
  return SVG_COMPOSITE_CONSTANTS.MATERIAL_TYPES.OVERLAY;
};

/**
 * ViewBox 인터페이스
 */
export interface ViewBox {
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * SVG ID들 인터페이스
 */
export interface SvgIds {
  cut: string;
  clip: string;
  filter: string;
}

/**
 * 필터 결과 이름 상수 (기본값)
 */
export const DEFAULT_FILTER_RESULTS = {
  MATERIAL: 'material',
  BLENDED: 'blended',
  FINAL: 'final',
} as const;

/**
 * 필터 결과 인터페이스
 */
export interface FilterResults {
  material: string;
  blended: string;
  final: string;
}

/**
 * @COMMENT - 속성 할당 추상화 함수들
 * DOM 요소에 속성을 설정하는 유틸리티 함수들
 */
export interface AttributeMap {
  [key: string]: string | number | boolean;
}

export interface NSAttributeMap {
  [namespace: string]: AttributeMap;
}

/**
 * @COMMENT - 일반 속성 설정 함수
 * SVG 요소에 일반 속성들을 일괄 설정합니다.
 *
 * 📝 상세 설명:
 * - Object.entries를 활용하여 속성들을 반복적으로 설정
 * - 모든 값들을 String()으로 변환하여 호환성 보장
 * - 코드 중복을 방지하고 가독성을 향상
 */
export const setAttributes = (element: Element, attributes: AttributeMap): void => {
  Object.entries(attributes).forEach(([key, value]) => {
    element.setAttribute(key, String(value));
  });
};

/**
 * @COMMENT - 네임스페이스 속성 설정 함수
 * SVG 요소에 네임스페이스가 있는 속성들을 설정합니다.
 *
 * 📝 상세 설명:
 * - xlink:href 등의 네임스페이스가 필요한 속성들을 처리
 * - SVG 표준 준수를 위해 네임스페이스별로 속성 설정
 * - 복잡한 네임스페이스 속성 설정 로직을 추상화
 */
export const setNSAttributes = (element: Element, nsAttributes: NSAttributeMap): void => {
  Object.entries(nsAttributes).forEach(([namespace, attributes]) => {
    Object.entries(attributes).forEach(([key, value]) => {
      element.setAttributeNS(namespace, key, String(value));
    });
  });
};

/**
 * @COMMENT - appendChild 추상화 함수들
 * DOM 트리에 요소들을 추가하는 유틸리티 함수들
 */

/**
 * @COMMENT - 다중 요소 일괄 추가 함수
 * 여러 자식 요소들을 부모 요소에 순서대로 추가합니다.
 *
 * 📝 상세 설명:
 * - forEach를 활용하여 배열의 모든 요소들을 순차적으로 추가
 * - 요소 생성과 추가 로직을 분리하여 코드 가독성 향상
 * - SVG 필터 체인 구성 시 유용하게 활용
 */
export const appendChildren = (parent: Element, children: Element[]): void => {
  children.forEach((child) => parent.appendChild(child));
};

/**
 * @COMMENT - 단일 요소 추가 함수
 * 하나의 자식 요소를 부모 요소에 추가합니다.
 *
 * 📝 상세 설명:
 * - 기본 DOM API의 appendChild를 추상화
 * - 함수형 프로그래밍 스타일 유지
 * - 코드 일관성을 위해 wrapper 함수로 제공
 */
export const appendChild = (parent: Element, child: Element): void => {
  parent.appendChild(child);
};

/**
 * @COMMENT - 스트로크 속성 복사 유틸리티 함수
 * SVG 요소에서 스트로크 관련 속성들을 복사하여 AttributeMap으로 반환
 *
 * 📝 상세 설명:
 * - 컷라인 요소의 시각적 속성들을 아웃라인 요소로 복사
 * - stroke, stroke-width, stroke-linecap 등 스트로크 관련 속성들만 추출
 * - null/undefined 값들은 필터링하여 깨끗한 AttributeMap 반환
 * - 아웃라인 효과 생성 시 필수적인 속성 복사 기능
 */
export const copyStrokeAttributes = (sourceElement: SVGPathElement): AttributeMap => {
  const strokeAttributes: AttributeMap = {};

  SVG_COMPOSITE_CONSTANTS.ATTRIBUTES.STROKE_PROPS.forEach((attr) => {
    const value = sourceElement.getAttribute(attr);
    if (value != null) {
      strokeAttributes[attr] = value;
    }
  });

  return strokeAttributes;
};

/**
 * @COMMENT - 재질 타입별 필터 요소 생성 함수들
 * 각 재질 타입에 맞는 SVG 필터 요소들을 생성하여 반환
 */

/**
 * @COMMENT - Overlay 타입용 필터 요소 생성
 * 원본 알파 채널만 유지하여 빈 영역을 투명하게 처리
 *
 * 📝 상세 설명:
 * - feComposite의 'in' 연산자를 사용하여 블렌드된 결과와 원본 알파 채널만 유지
 * - 재질이 적용된 영역 외부는 투명하게 처리되어 깔끔한 합성 효과
 * - 스티커 외곽선 내부에서만 재질이 보이도록 마스킹 효과
 */
export const createOverlayFilterElements = (doc: Document, filterResults: FilterResults): Element[] => {
  const feComp = doc.createElementNS(
    SVG_COMPOSITE_CONSTANTS.NAMESPACES.SVG,
    'feComposite',
  ) as SVGFECompositeElement;

  setAttributes(feComp, {
    in: filterResults.blended,
    in2: 'SourceAlpha',
    operator: 'in',
    result: filterResults.final,
  });

  return [feComp];
};

/**
 * @COMMENT - Multiply 타입용 필터 요소 생성
 * 배경 재질과 섞인 원본을 레이어로 병합
 *
 * 📝 상세 설명:
 * - feMerge를 사용하여 재질 이미지와 블렌드된 결과를 레이어로 쌓음
 * - 재질 이미지가 배경으로, 블렌드된 원본이 전면으로 배치
 * - 두 레이어가 자연스럽게 섞이는 multiply 효과 구현
 * - 더 풍부한 텍스처 표현을 위한 레이어 합성 방식
 */
export const createMultiplyFilterElements = (doc: Document, filterResults: FilterResults): Element[] => {
  const feMerge = doc.createElementNS(SVG_COMPOSITE_CONSTANTS.NAMESPACES.SVG, 'feMerge') as SVGFEMergeElement;

  // 첫 번째 노드: 재질 이미지 (배경 레이어)
  const n1 = doc.createElementNS(
    SVG_COMPOSITE_CONSTANTS.NAMESPACES.SVG,
    'feMergeNode',
  ) as SVGFEMergeNodeElement;
  setAttributes(n1, { in: filterResults.material });

  // 두 번째 노드: 블렌드된 결과 (전면 레이어)
  const n2 = doc.createElementNS(
    SVG_COMPOSITE_CONSTANTS.NAMESPACES.SVG,
    'feMergeNode',
  ) as SVGFEMergeNodeElement;
  setAttributes(n2, { in: filterResults.blended });

  // feMerge에 노드들 추가 (순서대로 렌더링)
  appendChildren(feMerge, [n1, n2]);
  setAttributes(feMerge, { result: filterResults.final });

  return [feMerge];
};

/**
 * @COMMENT - 재질 타입에 따른 필터 요소들을 생성하여 반환
 * 재질 타입별로 적절한 필터 요소들을 생성하는 디스패처 함수
 *
 * 📝 상세 설명:
 * - switch문을 활용하여 재질 타입별로 다른 필터 요소 생성
 * - overlay: 알파 채널 마스킹 방식
 * - multiply: 레이어 병합 방식
 * - 정의되지 않은 타입은 overlay로 fallback 처리
 * - 타입 안전성을 위한 switch-case 패턴 활용
 */
export const createMaterialTypeFilterElements = (
  doc: Document,
  materialType: MaterialType,
  filterResults: FilterResults,
): Element[] => {
  switch (materialType) {
    case SVG_COMPOSITE_CONSTANTS.MATERIAL_TYPES.OVERLAY:
      return createOverlayFilterElements(doc, filterResults);

    case SVG_COMPOSITE_CONSTANTS.MATERIAL_TYPES.MULTIPLY:
      return createMultiplyFilterElements(doc, filterResults);

    default:
      // 정의되지 않은 타입은 overlay로 처리 (안전한 fallback)
      console.warn(`알 수 없는 재질 타입 '${materialType}', overlay로 처리합니다`);
      return createOverlayFilterElements(doc, filterResults);
  }
};

/**
 * @COMMENT - viewBox를 안전하게 파싱하는 헬퍼 함수
 * SVG의 좌표계 정보를 안전하게 추출합니다.
 *
 * 📝 상세 설명:
 * - SVG의 viewBox는 좌표계 변환에 핵심적인 역할을 함
 * - 필터 효과의 위치와 크기를 정확하게 계산하기 위해 필요
 * - viewBox 파싱 실패 시 width/height를 fallback으로 사용
 * - 안전성을 위해 숫자 검증과 예외 처리를 포함
 * - SVG 표준 준수를 위한 좌표계 정보 추출
 */
export const parseViewBox = (svgElement: SVGSVGElement): ViewBox => {
  const viewBoxAttr = svgElement.getAttribute('viewBox');

  if (viewBoxAttr) {
    try {
      const parts = viewBoxAttr.trim().split(/\s+/);
      if (parts.length === 4 && parts[0] && parts[1] && parts[2] && parts[3]) {
        const x = Number(parts[0]);
        const y = Number(parts[1]);
        const width = Number(parts[2]);
        const height = Number(parts[3]);

        if (!isNaN(x) && !isNaN(y) && !isNaN(width) && !isNaN(height) && width > 0 && height > 0) {
          return { x: x || 0, y: y || 0, width, height };
        }
      }
    } catch (error) {
      console.warn('viewBox 파싱 실패:', viewBoxAttr, error);
    }
  }

  // viewBox가 없거나 파싱 실패 시 width/height 사용 (fallback)
  const widthStr = svgElement.getAttribute('width') || '0';
  const heightStr = svgElement.getAttribute('height') || '0';
  const width = parseFloat(widthStr);
  const height = parseFloat(heightStr);

  if (isNaN(width) || isNaN(height) || width <= 0 || height <= 0) {
    throw new Error('viewBox가 없는 경우 SVG는 유효한 width와 height 속성이 있어야 합니다');
  }

  return { x: 0, y: 0, width, height };
};

/**
 * @COMMENT - 고유한 ID들을 안전하게 생성
 * SVG 요소들의 고유 식별자를 생성합니다.
 *
 * 📝 상세 설명:
 * - SVG 요소들의 고유 식별자를 생성 (cut, clip, filter 용도별)
 * - nanoid를 사용하여 충돌 가능성이 극히 낮은 ID 생성
 * - 기존 Date.now() 방식보다 더 안전하고 예측 불가능
 * - 3글자로 짧게 유지하여 가독성과 성능 최적화
 * - 동시에 여러 SVG가 처리될 때 ID 충돌 방지
 */
export const generateUniqueIds = (): SvgIds => {
  // nanoid 3글자로 충분한 고유성 보장
  const uid = nanoid(3);

  return {
    cut: `cut-${uid}`, // 컷라인 패스 ID
    clip: `clip-${uid}`, // 클립패스 ID
    filter: `matfx-${uid}`, // 필터 ID
  };
};

/**
 * @COMMENT - 신뢰성 향상: 충돌 방지 고유 result 이름 생성
 * 여러 필터가 동시에 실행될 경우를 대비한 고유 result 이름 생성
 *
 * 📝 상세 설명:
 * - 필터 체인에서 각 단계의 result 이름을 고유하게 생성
 * - filterId를 기반으로 네임스페이스 분리하여 충돌 방지
 * - 동시에 여러 SVG 필터가 실행되어도 안전
 * - GPU 메모리에서 텍스처 충돌 방지
 * - 디버깅 시 필터 체인 추적이 용이
 */
export const generateUniqueFilterResults = (filterId?: string): FilterResults => {
  // 필터 ID를 기반으로 고유한 suffix 생성 (충돌 방지)
  const suffix = filterId || nanoid(3);

  return {
    material: `${DEFAULT_FILTER_RESULTS.MATERIAL}-${suffix}`, // 재질 입력 result
    blended: `${DEFAULT_FILTER_RESULTS.BLENDED}-${suffix}`, // 블렌드 결과 result
    final: `${DEFAULT_FILTER_RESULTS.FINAL}-${suffix}`, // 최종 결과 result
  };
};

/**
 * @COMMENT - SVG에서 컷라인 패스를 찾는 함수
 * 스티커의 외곽선을 정의하는 컷라인 패스를 검색합니다.
 *
 * 📝 상세 설명:
 * - 컷라인은 스티커의 외곽선을 정의하는 핵심 요소
 * - stroke-dasharray 속성이 있는 path를 우선 찾음 (점선 스타일 컷라인)
 * - 일반 path를 fallback으로 사용
 * - 컷라인이 없으면 합성 자체가 불가능하므로 null 반환 시 에러 처리
 * - 디자인 패턴 인식 알고리즘 기반 검색
 */
export const findCutPath = (svg: SVGSVGElement): SVGPathElement | null => {
  // stroke-dasharray가 있는 패스를 우선 찾음
  const dashedPath = svg.querySelector(SVG_COMPOSITE_CONSTANTS.SELECTORS.CUT_PATH) as SVGPathElement;
  if (dashedPath) return dashedPath;

  // 일반 path를 찾음
  const path = svg.querySelector('path') as SVGPathElement;
  if (path) return path;

  return null;
};

/**
 * @COMMENT - defs 요소를 생성하거나 기존 요소를 찾는 함수
 * SVG 정의 요소들을 담을 컨테이너를 준비합니다.
 *
 * 📝 상세 설명:
 * - defs는 SVG에서 재사용 가능한 정의 요소들을 담는 컨테이너
 * - 필터, 클립패스, 심볼 등의 재사용 요소들을 한 곳에 모아둠
 * - xmlns:xlink 네임스페이스 추가로 xlink 속성 지원 보장
 * - SVG 표준 준수를 위해 defs를 SVG 시작 부분에 배치
 * - 기존 defs가 없으면 새로 생성하여 추가
 */
export const ensureDefsElement = (svg: SVGSVGElement, doc: Document): SVGDefsElement => {
  let defs = svg.querySelector('defs') as SVGDefsElement;
  if (!defs) {
    defs = doc.createElementNS(SVG_COMPOSITE_CONSTANTS.NAMESPACES.SVG, 'defs') as SVGDefsElement;
    // defs를 SVG의 첫 번째 자식으로 추가
    const firstChild = svg.firstChild;
    if (firstChild) {
      svg.insertBefore(defs, firstChild);
    } else {
      appendChild(svg, defs);
    }
  }

  // xmlns:xlink 속성 추가
  if (!svg.getAttribute('xmlns:xlink')) {
    setAttributes(svg, {
      'xmlns:xlink': SVG_COMPOSITE_CONSTANTS.NAMESPACES.XLINK,
    });
  }

  return defs;
};

/**
 * @COMMENT - 컷라인과 클립패스를 생성하는 함수
 * 컷라인을 기반으로 클립패스를 생성하여 재질 마스킹을 준비합니다.
 *
 * 📝 상세 설명:
 * - 컷라인 path를 defs에 정의하여 재사용 가능하게 만듦
 * - 클립패스를 생성하여 재질을 스티커 모양대로 마스킹
 * - use 요소로 컷라인을 참조하여 클립패스 구성
 * - 이렇게 하면 재질 이미지가 스티커 외곽선 내에서만 표시됨
 * - 재사용 가능한 클립패스 생성으로 메모리 효율성 향상
 */
export const createCutAndClipPaths = (
  doc: Document,
  cutPath: SVGPathElement,
  ids: SvgIds,
  defs: SVGDefsElement,
): void => {
  // 컷라인 재사용을 위한 path 정의
  const cutDef = doc.createElementNS(SVG_COMPOSITE_CONSTANTS.NAMESPACES.SVG, 'path') as SVGPathElement;
  setAttributes(cutDef, {
    id: ids.cut,
    d: cutPath.getAttribute('d') || '',
  });

  // 클립패스 생성
  const clip = doc.createElementNS(SVG_COMPOSITE_CONSTANTS.NAMESPACES.SVG, 'clipPath') as SVGClipPathElement;
  setAttributes(clip, { id: ids.clip });

  const useForClip = doc.createElementNS(SVG_COMPOSITE_CONSTANTS.NAMESPACES.SVG, 'use') as SVGUseElement;
  setNSAttributes(useForClip, {
    [SVG_COMPOSITE_CONSTANTS.NAMESPACES.XLINK]: {
      'xlink:href': `#${ids.cut}`,
    },
  });

  // 요소들 defs에 추가 (추상화 적용)
  appendChildren(clip, [useForClip]);
  appendChildren(defs, [cutDef, clip]);
};

/**
 * @COMMENT - 재질 이미지를 데이터 URI로 변환하는 헬퍼 함수
 * 외부 URL 이미지를 SVG 내에서 사용할 수 있는 base64 data URI로 변환합니다.
 *
 * 📝 상세 설명:
 * - 외부 URL 이미지를 base64 data URI로 변환하여 SVG에 embed
 * - SVG 내에서 이미지를 직접 포함하기 위해 필수적인 변환 과정
 * - 브라우저 호환성과 보안성을 위해 data URI 포맷 사용
 * - ImageUtil.urlToDataUri를 활용하여 일관된 변환 로직 유지
 * - CORS 제한 없이 SVG 필터에서 이미지 사용 가능
 */
export const urlToDataUri = async (url: string): Promise<string> => {
  return ImageUtil.urlToDataUri(url);
};

/**
 * @COMMENT - 재질 필터를 생성하는 헬퍼 함수
 * SVG 필터 체인을 구성하여 재질 이미지와 원본 디자인을 자연스럽게 합성합니다.
 *
 * 📝 상세 설명:
 * - feImage: 재질 이미지를 필터 입력으로 설정
 * - feBlend: 재질과 원본을 지정된 모드로 합성 (overlay/multiply)
 * - 타입별 후처리: overlay는 알파 채널 마스킹, multiply는 레이어 병합
 * - viewBox 크기에 맞춰 필터 영역을 정확하게 설정
 * - sRGB 색상 보간법으로 브라우저간 일관된 색상 표현 보장
 * - 고유 result 이름으로 다중 필터 실행 시 충돌 방지
 */
export const createMaterialFilter = (
  doc: Document,
  viewBox: ViewBox,
  dataUri: string,
  materialType: MaterialType,
  ids: SvgIds,
  defs: SVGDefsElement,
): void => {
  const filter = doc.createElementNS(SVG_COMPOSITE_CONSTANTS.NAMESPACES.SVG, 'filter') as SVGFilterElement;

  // 🎯 신뢰성 향상: 고유 result 이름 생성 (충돌 방지)
  const filterResults = generateUniqueFilterResults(ids.filter);

  // 필터 속성 설정 (추상화 적용)
  setAttributes(filter, {
    id: ids.filter,
    filterUnits: 'userSpaceOnUse',
    x: viewBox.x,
    y: viewBox.y,
    width: viewBox.width,
    height: viewBox.height,
    style: 'color-interpolation-filters:sRGB',
  });

  // 재질 입력 feImage 생성
  const feImg = doc.createElementNS(SVG_COMPOSITE_CONSTANTS.NAMESPACES.SVG, 'feImage') as SVGFEImageElement;
  setAttributes(feImg, {
    x: viewBox.x,
    y: viewBox.y,
    width: viewBox.width,
    height: viewBox.height,
    preserveAspectRatio: 'xMidYMid slice',
    result: filterResults.material,
  });
  setNSAttributes(feImg, {
    [SVG_COMPOSITE_CONSTANTS.NAMESPACES.XLINK]: {
      href: dataUri,
      'xlink:href': dataUri,
    },
  });

  // 재질 블렌드 feBlend 생성
  const feBlend = doc.createElementNS(SVG_COMPOSITE_CONSTANTS.NAMESPACES.SVG, 'feBlend') as SVGFEBlendElement;
  setAttributes(feBlend, {
    mode: materialType,
    in: 'SourceGraphic', // 필터 대상(원본 use)
    in2: filterResults.material,
    result: filterResults.blended,
  });

  // 재질 타입별 필터 요소들 생성 (분리된 함수 활용)
  const additionalElements = createMaterialTypeFilterElements(doc, materialType, filterResults);

  // 필터에 모든 요소들 추가 (추상화 적용)
  appendChildren(filter, [feImg, feBlend, ...additionalElements]);
  appendChild(defs, filter);
};

/**
 * @COMMENT - 클립 그룹과 필터링된 요소를 생성하는 헬퍼 함수
 * 필터링된 디자인 요소를 클립패스로 마스킹하여 최종 합성 결과를 생성합니다.
 *
 * 📝 상세 설명:
 * - 클립패스로 재질을 스티커 모양대로 정확하게 마스킹
 * - 필터링된 디자인 요소를 그룹으로 묶어 클리핑 적용
 * - isolation:isolate로 필터 효과의 독립성 보장 (다른 요소에 영향 미치지 않음)
 * - 원본 use 요소를 복제하여 필터를 적용한 새 요소 생성
 * - 클립 그룹을 SVG에 추가하여 최종 렌더링 결과 생성
 */
export const createClippedGroup = (doc: Document, svg: SVGSVGElement, ids: SvgIds): void => {
  const clipGroup = doc.createElementNS(SVG_COMPOSITE_CONSTANTS.NAMESPACES.SVG, 'g') as SVGGElement;
  setAttributes(clipGroup, {
    'clip-path': `url(#${ids.clip})`,
    style: 'isolation:isolate',
  });

  // 원본 use 요소 찾기 및 복제
  const origUse = svg.querySelector(SVG_COMPOSITE_CONSTANTS.SELECTORS.DESIGN_USE) as SVGUseElement;
  if (origUse) {
    const filtered = origUse.cloneNode(true) as SVGUseElement;
    setAttributes(filtered, {
      filter: `url(#${ids.filter})`,
    });
    appendChild(clipGroup, filtered);
  }

  appendChild(svg, clipGroup);
};

/**
 * @COMMENT - 아웃라인 요소를 추가하는 헬퍼 함수
 * 컷라인을 따라 시각적 외곽선 효과를 추가하여 스티커의 경계를 명확하게 표시합니다.
 *
 * 📝 상세 설명:
 * - 컷라인을 따라 시각적 외곽선 효과 추가로 스티커 경계 명확화
 * - 원본 컷라인의 모든 스트로크 속성을 복사하여 일관된 스타일 유지
 * - vector-effect: non-scaling-stroke로 줌 레벨에 관계없이 일정한 선 굵기 유지
 * - mix-blend-mode: normal로 블렌딩 모드 지정하여 자연스러운 외곽선 효과
 * - use 요소로 컷라인을 재참조하여 DRY 원칙 준수
 */
export const addOutlineElement = (
  doc: Document,
  svg: SVGSVGElement,
  cutPath: SVGPathElement,
  ids: SvgIds,
): void => {
  const outlineTop = doc.createElementNS(SVG_COMPOSITE_CONSTANTS.NAMESPACES.SVG, 'use') as SVGUseElement;

  // 기본 속성 설정
  setAttributes(outlineTop, {
    fill: 'none',
    'vector-effect': 'non-scaling-stroke',
    style: 'mix-blend-mode:normal',
  });

  // 네임스페이스 속성 설정
  setNSAttributes(outlineTop, {
    [SVG_COMPOSITE_CONSTANTS.NAMESPACES.XLINK]: {
      'xlink:href': `#${ids.cut}`,
    },
  });

  // 스트로크 속성 복사
  const strokeAttributes = copyStrokeAttributes(cutPath);
  setAttributes(outlineTop, strokeAttributes);

  appendChild(svg, outlineTop);
};
